import type { BoxListItem } from "../../../api/WarehouseApi";
import { Date as WarehouseDate } from "../../../stores/Date";
import type { Product } from "../../../stores/Product";
import { type IDate } from "../../../types/date";
import { type Measures } from "../../../types/entities";
import { BOX_ID_PATTERN } from "../../../domain/entity-ids";
import {
  isValidPhotoUrl,
  normalizeProductPhotoState,
} from "../../../domain/product-photos";
import {
  isExpiryBeforeManufacturing,
  parseInputDate,
  parseQuantityInput,
} from "../../../domain/product-form";
import { normalizeTypeTags } from "../../../utils/typeTags";
import { resolveBoxReferenceInput } from "../../../utils/boxReference";

export interface EditProductFormState {
  name: string;
  description: string;
  photo: string;
  photos: string[];
  typeTags: string[];
  parentId: string;
  manufacturingDate: string;
  expiryDate: string;
  measure: Measures;
  quantityInput: string;
}

export interface EditProductUpdatePayload {
  name: string;
  photo: string;
  photos?: string[];
  description: string;
  typeTags?: string[];
  measure: Measures;
  quantity: number;
  parentId: string | null;
  manufacturingDate: IDate | null;
  expiryDate: IDate | null;
}

export function resolveParentId(
  parentIdInput: string,
  availableBoxes: BoxListItem[],
): string {
  return resolveBoxReferenceInput(parentIdInput, availableBoxes);
}

export function validateEditProductForm(
  state: EditProductFormState,
  availableBoxes: BoxListItem[],
): string | null {
  if (!state.name.trim()) {
    return "Укажите название продукта";
  }

  if (state.typeTags.length === 0) {
    return "Добавьте хотя бы один тег типа";
  }

  const parentId = resolveParentId(state.parentId, availableBoxes);
  if (parentId && !BOX_ID_PATTERN.test(parentId)) {
    return "Выберите существующую коробку из списка";
  }

  if (
    parentId &&
    availableBoxes.length > 0 &&
    !availableBoxes.some((box) => box.id === parentId)
  ) {
    return "Выберите существующую коробку из списка";
  }

  const photoState = normalizeProductPhotoState({
    photo: state.photo,
    photos: state.photos,
  });
  const invalidPhoto = photoState.photos.find(
    (photo) => !isValidPhotoUrl(photo),
  );
  if (invalidPhoto) {
    return "Каждое фото должно быть корректным URL";
  }

  if (!parseQuantityInput(state.quantityInput)) {
    return "Количество должно быть числом больше 0";
  }

  if (
    state.manufacturingDate.trim() &&
    !parseInputDate(state.manufacturingDate)
  ) {
    return "Некорректная дата производства";
  }

  if (state.expiryDate.trim() && !parseInputDate(state.expiryDate)) {
    return "Некорректный срок годности";
  }

  const manufacturingDate = parseInputDate(state.manufacturingDate);
  const expiryDate = parseInputDate(state.expiryDate);

  if (isExpiryBeforeManufacturing(manufacturingDate, expiryDate)) {
    return "Срок годности не может быть раньше даты производства";
  }

  return null;
}

export function buildEditProductUpdatePayload(
  state: EditProductFormState,
  availableBoxes: BoxListItem[],
): EditProductUpdatePayload {
  const parentId = resolveParentId(state.parentId, availableBoxes);
  const quantity = parseQuantityInput(state.quantityInput) || 1;
  const photoState = normalizeProductPhotoState({
    photo: state.photo,
    photos: state.photos,
  });

  return {
    name: state.name.trim(),
    description: state.description.trim(),
    photo: photoState.photo,
    photos: photoState.photos,
    typeTags: state.typeTags,
    parentId: parentId || null,
    measure: state.measure,
    quantity,
    manufacturingDate: parseInputDate(state.manufacturingDate),
    expiryDate: parseInputDate(state.expiryDate),
  };
}

export function applyUpdatedProduct(
  target: Product,
  updated: EditProductUpdatePayload,
): void {
  const photoState = normalizeProductPhotoState({
    photo: updated.photo,
    photos: updated.photos,
  });

  target.name = updated.name;
  target.photo = photoState.photo;
  target.photos = photoState.photos;
  target.description = updated.description;
  target.typeTags = normalizeTypeTags(updated.typeTags);
  target.parentId = updated.parentId;
  target.measure = updated.measure;
  target.quantity = updated.quantity;
  target.manufacturingDate = updated.manufacturingDate
    ? new WarehouseDate(updated.manufacturingDate)
    : null;
  target.expiryDate = updated.expiryDate
    ? new WarehouseDate(updated.expiryDate)
    : null;
}
